import { useEffect, useSyncExternalStore } from "react";

import {
  getDeviceAccount,
  getDeviceSetting,
  setDeviceAccount,
  setDeviceSetting,
  useDeviceAccount,
} from "@-/dev.improve.haniverse.lib.device-settings";
import {
  buildProvisionedDeviceAccount,
  discardGuestHandoffDatabase,
  getActiveActorTursoSync,
  guestReplayRunsDuringOpen,
  isActorSyncProvisioned,
  prepareActorReplicaRebuild,
  resetActorDatabaseForSignIn,
  resetActorDatabaseForSignOut,
  retireActorReplicaForRebuild,
  type ActorReplicaRebuildPrepared,
} from "@-/dev.improve.haniverse.database.actor";
import {
  closeCachedActorDatabaseConnection,
  getCachedActorDatabase,
  loadActorDatabase,
  rebuildActorDatabase,
  reopenActorDatabaseConnectionInSyncMode,
  runWithActorDatabaseLoadsPaused,
} from "@-/dev.improve.haniverse.contexts.database.actor";
import { getAuthClient } from "@-/dev.improve.haniverse.lib.auth.client";
import { runOwnedSqliteTransaction } from "@-/dev.improve.haniverse.lib.database.engine";
import type { GuestHandoffCarried } from "@-/dev.improve.haniverse.database.actor";
import { createPlainConsola } from "@-/dev.improve.haniverse.lib.logger";
import onetime from "@-/com.npmjs.onetime";
import delay from "@-/com.npmjs.delay";
import ensureError from "@-/com.npmjs.ensure-error";
import pMemoize from "@-/com.npmjs.p-memoize";
import pRetry from "@-/com.npmjs.p-retry";
import { isDeadlineExceededError, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { getOrpcClient } from "@-/dev.improve.haniverse.lib.orpc";
import { useQueryClient, type QueryClient } from "@-/com.npmjs.tanstack__react-query";

import { installActorReadGate } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { clearActorUserCache } from "@-/dev.improve.haniverse.lib.user";
import { resetCrispChatSession } from "@-/dev.improve.haniverse.lib.crisp";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import {
  clearSyncFailure,
  getInitialPullCompletionCount,
  setSyncFailure,
  useSyncStatus,
  waitForInitialPullCompletion,
} from "#sync-status";
import { publishAccountChange, useCrossTabAccountChanges } from "#cross-tab-account-change";
import {
  assertEveryLocalChangeUploaded,
  UnsyncedStudyDataError,
} from "#assert-every-local-change-uploaded";
import { ACTOR_SYNC_PROTOCOL } from "@-/dev.improve.haniverse.lib.database.actor.migration-tags";
import {
  usesNativeBackupEngine,
  withActorBackupCheckpoint,
} from "@-/dev.improve.haniverse.lib.actor-backups";
import { unregisterSyncPush } from "@-/dev.improve.haniverse.lib.sync-push";

export interface ActorSyncState {
  isPreparingActorSync: boolean;
}

// Set when the user authenticates via SIGN-IN (existing account), cleared when
// consumed by the reload below. On sign-in the server is the source of truth, so
// the local actor.db (opened local-only + migrated at signed-out launch) is wiped
// before the reload so the next sync-mode open BOOTSTRAPS the account's cloud data
// from an empty file. NOT set on sign-UP, where local study-before-sign-in must be
// preserved + pushed. Module-level (not React state) so it survives from the form
// submit to this provisioning effect, and a single boolean since only one
// auth→provision→reload cycle is ever in flight.
let pendingSignInActorReset = false;

// Set when the user authenticates via SIGN-UP, cleared when consumed. The mirror of
// `pendingSignInActorReset`: instead of wiping local study, the signed-out guest
// database is replayed INTO the freshly provisioned account and pushed. Native
// also persists the handoff on the device account and finishes it during open.
let pendingGuestReplay = false;

// A signal bumped whenever an auth event (sign-in / sign-up / email-verify) establishes a session,
// and cleared to 0 when sign-out ends it. `useActorSync` subscribes to this so its provisioning
// effect re-runs on the bump — WITHOUT depending on `getAuthClient().useSession()` becoming
// reactive. On the Android RELEASE build that session hook does NOT propagate the new `userId`
// after an in-session sign-in (verified via logcat: the provisioning effect only ever ran with
// `userId=null`), so a `userId`-gated effect never fired → no `database.actor.ensure` → the actor DB
// stayed in local-only mode → empty deck. Provisioning doesn't actually NEED the client userId:
// `database.actor.ensure` authenticates via the session COOKIE server-side, and the cookie is
// durably set by the time this fires (the effect awaits `waitForSessionCookie`). So we bump this
// signal on the auth-success path and provision off it directly.
let authChangeSignal = 0;

/**
 * When the last replica rebuild started. A rebuild downloads the whole account again, so two may
 * never run back to back: whatever asks for one within this floor of the last is answered by the
 * last one. The supervisor spaces its own requests an hour apart; this is the backstop for every
 * other caller (the pending marker at sync start, a sign-in that finds one).
 */
let lastReplicaRebuildStartedAt = Number.NEGATIVE_INFINITY;
const REPLICA_REBUILD_FLOOR_MS = 2 * 60_000;
const authChangeListeners = new Set<() => void>();

function subscribeToAuthChange(listener: () => void): () => void {
  authChangeListeners.add(listener);
  return () => authChangeListeners.delete(listener);
}

function getAuthChangeSnapshot(): number {
  return authChangeSignal;
}

/** Notify `useActorSync` subscribers that a session was just established. */
function bumpAuthChangeSignal(): void {
  authChangeSignal += 1;
  for (const listener of authChangeListeners) {
    listener();
  }
}

/**
 * The session ended (sign-out): `useActorSync` must stop treating this runtime as signed in.
 * Otherwise the reset's `setDeviceAccount(null)` met a still-raised signal, which reads as "signed
 * in without an account", and provisioning restarted right after sign-out — four "Not signed in"
 * `database.actor.ensure` failures and a sync-failure banner on a device that had just signed out.
 */
function clearAuthChangeSignal(): void {
  authChangeSignal = 0;
  for (const listener of authChangeListeners) {
    listener();
  }
}

// Hard ceiling on the sign-in read gate installed below. Must cover the whole
// bootstrap (session-cookie wait + `database.actor.ensure` retries + connection
// swap + initial pull) in the common case, but stay bounded so a wedged backend
// can't hold every actor query in its loading state forever — on timeout the
// gated reads resolve against whatever synced, the failure sits in sync-status,
// and background sync + the standard invalidation chain fill in the deck when it
// eventually lands.
const SIGN_IN_READ_GATE_TIMEOUT_MS = 60_000;

// ---- Deadlines for every step of provisioning, sign-in and sign-out ----
//
// Nothing in the account flows may wait forever: the sign-in placeholder, the auth forms and the
// sign-out button all sit on these steps. Each step is bounded at its own source, and the
// single-flights that share them carry an outer backstop so a step bound that fails anyway still
// releases the memo entry (otherwise every later caller would join the same endless run).

/** One `getSession` request that reads the stored cookie back from the auth server. */
const SESSION_CHECK_DEADLINE_MS = 5000;
/**
 * The whole post-sign-in cookie wait. The SecureStore write settles in well under a second; the
 * old 10 × 200ms retry budget plus a few requests fits comfortably.
 */
const SESSION_COOKIE_WAIT_DEADLINE_MS = 10_000;
/**
 * Hard per-attempt ceiling for `database.actor.ensure`. A request the backend never answers
 * (observed live: the staging worker's wedged migration memo held every ensure open until the CDN
 * killed it at ~100s) must become a retryable failure, since RN's fetch has no default timeout.
 * 20s is several times a healthy ensure, including a brand-new account's database creation.
 */
const ENSURE_ATTEMPT_DEADLINE_MS = 20_000;
const ENSURE_MAX_ATTEMPTS = 4;
/**
 * The whole provisioning request phase: cookie wait + every ensure attempt + backoff. This is how
 * long "Syncing data from cloud" can show BEFORE the account exists; on expiry the failure lands
 * in sync-status (kind `push`), which ends that placeholder. It was ~165s (5 × 30s + waits) and
 * unbounded before the first attempt.
 */
const PROVISIONING_REQUEST_DEADLINE_MS = 90_000;
/**
 * Opening the actor database: the Turso connect (`CONNECT_TIMEOUT_MS` 20s × 3 attempts in the
 * engine), the bootstrap of a fresh replica, migrations and (native) the guest replay during open.
 */
const ACTOR_DATABASE_OPEN_DEADLINE_MS = 90_000;
/**
 * Close the local-only handle, save its safety backup, reset its files and reopen in sync mode:
 * local file operations plus waiting for in-flight statements to finish on the old handle. The
 * backup is a file copy (well under a second); its own native steps are bounded separately.
 */
const DATABASE_SWAP_DEADLINE_MS = 90_000;
/** Building and writing the guest replay: local reads/writes of one small database. */
const GUEST_REPLAY_DEADLINE_MS = 30_000;
/** Pushing the replayed guest rows: one upload request (matches the manual push bound). */
const GUEST_REPLAY_PUSH_DEADLINE_MS = 45_000;
/**
 * Backstop for the provisioning single-flight: the request phase plus every step of the
 * connection swap above (backup checkpoint ≤60s without progress, swap, cookie, open, replay).
 * Reached only if a step bound itself fails; it releases the single-flight for the next caller.
 */
const ACTOR_SYNC_PROVISIONING_DEADLINE_MS = 6 * 60_000;
/** Backstop for the sign-in bootstrap single-flight: provisioning plus the initial-pull wait. */
const SIGN_IN_BOOTSTRAP_DEADLINE_MS = ACTOR_SYNC_PROVISIONING_DEADLINE_MS + 30_000;
/**
 * How long sign-out waits for an in-flight provisioning (which may be carrying a sign-up's guest
 * study into the account) before ending the session anyway. Covers a normal provisioning
 * (one or two ensure attempts); sign-out must always complete, so it never waits for a wedged one.
 */
const SIGN_OUT_PROVISIONING_WAIT_MS = 30_000;
/** Ending the server session: one auth request. */
const SIGN_OUT_REQUEST_DEADLINE_MS = 20_000;
/**
 * Proving the cloud holds this device's study before sign-out erases it: usually one push and one
 * count, but a device whose sign-in never provisioned first gets its account database here (the
 * cookie wait, the `database.actor.ensure` attempts, the sync-mode reopen and its guest replay).
 */
const SIGN_OUT_UPLOAD_DEADLINE_MS = 3 * 60_000;
/** The re-check just before the connection closes: normally one empty push and one count. */
const SIGN_OUT_FINAL_UPLOAD_DEADLINE_MS = 30_000;
/**
 * The clean-install reset: the final upload check, closing the actor database, ending the server
 * session, then purging and reopening it (45s for the local file work).
 */
const SIGN_OUT_RESET_DEADLINE_MS =
  SIGN_OUT_FINAL_UPLOAD_DEADLINE_MS + SIGN_OUT_REQUEST_DEADLINE_MS + 45_000;

const actorReadGateState: {
  release: (() => void) | null;
  timeout: ReturnType<typeof setTimeout> | null;
} = {
  release: null,
  timeout: null,
};

/**
 * Hold every actor-table read until the sign-in deck bootstrap settles. Installed
 * the moment a sign-in succeeds (before the wipe can happen), released when the
 * the full account bootstrap completes/fails/times out. For sign-up, "full"
 * includes replaying and pushing guest data AFTER the initial pull; releasing on
 * the pull alone lets a hard navigation abort the replay's WASM fetch and leaves
 * the new account without its local study rows. While held, screens
 * that read actor data sit in their ordinary React Query loading/Suspense state —
 * no sync-aware special casing — so a freshly-signed-in user never sees the
 * momentarily-empty post-wipe database (e.g. the Learn summary's new-user
 * welcome view flashing at a returning user).
 *
 * Self-owned: the release watcher is module-level and bounded, so it does not
 * depend on any component staying mounted.
 */
function holdActorReadsForSignInBootstrap(): void {
  // The gate enforces the same ceiling itself (it fails open at `maxHoldMs`); the timer below is
  // what normally releases it on timeout, so the release is logged and the state reset.
  actorReadGateState.release ??= installActorReadGate({ maxHoldMs: SIGN_IN_READ_GATE_TIMEOUT_MS });
  if (actorReadGateState.timeout === null) {
    const timeout = setTimeout(() => {
      releaseActorReadsForSignInBootstrap("timeout");
    }, SIGN_IN_READ_GATE_TIMEOUT_MS);
    // Jest exercises the auth markers without running a browser bootstrap. The
    // fail-open timer must not keep that Node worker alive; browsers return a
    // numeric timer id and simply skip this branch.
    const unref: unknown =
      typeof timeout === "object" && timeout !== null ? Reflect.get(timeout, "unref") : undefined;
    if (typeof unref === "function") {
      Reflect.apply(unref, timeout, []);
    }
    actorReadGateState.timeout = timeout;
  }
}

function releaseActorReadsForSignInBootstrap(outcome: string): void {
  const release = actorReadGateState.release;
  if (release === null) {
    return;
  }
  actorReadGateState.release = null;
  if (actorReadGateState.timeout !== null) {
    clearTimeout(actorReadGateState.timeout);
    actorReadGateState.timeout = null;
  }
  getSyncLog().info("sign-in actor read gate released", { outcome });
  release();
}

/**
 * Reset active queries without deadlocking the sign-in read gate.
 *
 * `resetQueries()` resolves only after its active queries refetch. During account
 * bootstrap those actor-table refetches intentionally wait behind the read gate,
 * while the gate itself is released only after bootstrap completes. Awaiting the
 * reset in that state creates a cycle that can only be broken by the fail-open
 * timer. Start the reset so it re-suspends the UI and opens the replacement
 * connection, but let the explicit sync-handle / initial-pull waits below govern
 * bootstrap completion. Once the gate releases, the reset finishes normally.
 */
async function resetQueriesDuringActorBootstrap(queryClient: QueryClient): Promise<void> {
  const reset = queryClient.resetQueries();
  if (actorReadGateState.release === null) {
    await reset;
    return;
  }
  void reset.catch((error: unknown) => {
    getSyncLog().error("actor query reset failed after bootstrap", error);
  });
}

/**
 * Reset every query (the cache is cleared synchronously) and let the active ones refetch in the
 * background. `resetQueries()` resolves only after EVERY active query has refetched, so awaiting
 * it made sign-out and the manual cloud actions wait on unrelated screens' reads — any one of
 * which could be slow or stuck. Nothing after the reset depends on those refetches.
 */
function startQueryReset(queryClient: QueryClient, reason: string): void {
  void queryClient.resetQueries().catch((error: unknown) => {
    getSyncLog().error(`query reset after ${reason} failed`, error);
  });
}

/**
 * Wait (bounded) for in-flight provisioning to settle. Returns whether it did; on expiry the
 * caller proceeds and the straggler is logged, never awaited further.
 */
async function waitForInFlightActorSyncProvisioning(reason: string): Promise<boolean> {
  try {
    await withDeadline(
      {
        deadline: SIGN_OUT_PROVISIONING_WAIT_MS,
        operation: `in-flight actor sync provisioning (${reason})`,
      },
      () => Promise.allSettled(inFlightActorSyncProvisioning),
    );
    return true;
  } catch (error) {
    getSyncLog().warn(`${reason}: proceeding without in-flight provisioning`, error);
    return false;
  }
}

/**
 * Mark that the just-completed auth was a SIGN-IN (not sign-up), so the actor DB
 * is wiped before the sync-mode reload. Call from the auth form's sign-in success
 * path only. Also bumps the auth-change signal so `useActorSync` provisions even
 * when the reactive session hook doesn't update (Android Release — see above),
 * and gates actor reads until the account's deck lands (see
 * `holdActorReadsForSignInBootstrap`).
 */
export function markSignInForActorReset(): void {
  pendingGuestReplay = false;
  pendingSignInActorReset = true;
  if (!usesNativeBackupEngine) {
    holdActorReadsForSignInBootstrap();
  }
  bumpAuthChangeSignal();
}

/**
 * Notify `useActorSync` that a session was just established by a NON-sign-in auth flow
 * (sign-up / email-verify), so it provisions the per-user Turso DB even if the reactive
 * `useSession()` hook doesn't update. Does NOT mark the sign-in actor reset (sign-up must
 * preserve + push local study).
 */
export function notifyAuthSessionEstablished(): void {
  bumpAuthChangeSignal();
}

/**
 * Mark that the just-completed auth was a SIGN-UP, so the signed-out `guest.db`
 * study data is REPLAYED into the newly provisioned account and pushed, instead of
 * being discarded.
 *
 * An explicit signup wins over `created: false`: a retried provisioning request
 * can find the database created by the first request. That does not turn this
 * signup into a sign-in or revoke the obligation to carry over local study.
 */
export function markSignUpForGuestReplay(): void {
  pendingSignInActorReset = false;
  pendingGuestReplay = true;
  holdActorReadsForSignInBootstrap();
  bumpAuthChangeSignal();
}

/**
 * Should this provisioning replay local guest data into the account?
 *
 * `created === true`  → the server just created this account's database: SIGN-UP.
 * `created === false` → existing database, possibly created by an earlier retry.
 * `created === undefined` → the server can't tell (local-tursodb dev). Fall back to
 *   the client-side hint set by the auth form.
 */
export function isSignUpProvisioning(created: boolean | undefined): boolean {
  return pendingGuestReplay || created === true;
}

/**
 * Would erasing this device lose study the account's cloud database lacks? Only a device that
 * keeps account study locally: native always (its sign-out deletes every actor file), web once it
 * has an account database (its next sign-in wipes `actor.db`). An unprovisioned web device studies
 * in `guest.db`, which neither sign-out nor sign-in deletes.
 */
function holdsAccountStudyLocally(): boolean {
  return getDeviceAccount() != null || guestReplayRunsDuringOpen;
}

/**
 * The ONE sign-out sequence after any safety backup: prove the cloud holds this device's study,
 * then return the device to a clean install, ending the server session on the way. Shared by the
 * account page and the database-failure screen so the two can never diverge.
 *
 * Erasing study that never reached the cloud is how a review made while sync was failing vanished
 * across sign-out → sign-in (Leon's phone, 2026-10-02: every sign-in that day failed to provision,
 * and each sign-out deleted everything studied since). So unless the caller passes the user's
 * explicit `discardUnsyncedStudy` choice, this uploads every local change first — provisioning a
 * device whose sign-in never received its account database — and throws
 * {@link UnsyncedStudyDataError}, before anything is ended or erased, when it cannot prove the
 * upload.
 */
export async function signOutAndResetDevice(
  queryClient: QueryClient,
  { discardUnsyncedStudy }: { discardUnsyncedStudy: boolean },
): Promise<void> {
  // Settle an in-flight provisioning first (see `resetActorSyncForSignOut` for why it must not be
  // interrupted); the upload below may need the account it is creating.
  await waitForInFlightActorSyncProvisioning("sign-out");
  let imported: ActorReplicaRebuildPrepared | null = null;
  if (!discardUnsyncedStudy && holdsAccountStudyLocally()) {
    try {
      // Needs the live session: an unprovisioned device can only get its account database with it.
      await assertEveryLocalChangeUploaded(
        async () =>
          (await ensureActorSyncConnected(queryClient)) ? getActiveActorTursoSync() : null,
        { deadlineMs: SIGN_OUT_UPLOAD_DEADLINE_MS },
      );
    } catch (error) {
      if (!(error instanceof UnsyncedStudyDataError)) {
        throw error;
      }
      // The engine could not upload. The import is the other way out of a replica (see
      // `database.md`, "Server: import endpoint"): the study reaches the account through it, or
      // the sign-out stops here with the study kept, as before.
      imported = await carryStudyThroughImport(error);
    }
  }
  // While the session is still valid: stop the server from pushing this install for the account.
  // Best-effort; a stale registration only bumps a counter until the next account takes it over.
  await unregisterSyncPush().catch((error: unknown) => {
    getSyncLog().warn("sync push unregistration failed during sign-out", error);
  });
  await resetActorSyncForSignOut(queryClient, { discardUnsyncedStudy, imported });
}

async function carryStudyThroughImport(
  uploadFailure: UnsyncedStudyDataError,
): Promise<ActorReplicaRebuildPrepared> {
  const connection = getCachedActorDatabase()?.connection ?? null;
  if (!isActorSyncProvisioned(getDeviceAccount())) {
    throw uploadFailure;
  }
  let prepared: ActorReplicaRebuildPrepared;
  try {
    prepared = await prepareActorReplicaRebuild("sign-out", connection);
  } catch (error) {
    throw new UnsyncedStudyDataError(
      `the upload failed, and so did the import: ${ensureError(error).message}`,
      {
        cause: error,
      },
    );
  }
  if (prepared.result.source === "unreadable") {
    throw uploadFailure;
  }
  getSyncLog().warn("sign-out carried the study through the import", prepared.result);
  return prepared;
}

/**
 * Sign-out returns the device's study state to a CLEAN INSTALL (Leon, 2026-09-22): every actor
 * database file — the live one, retired and quarantined generations, an unreplayed guest handoff
 * — is deleted, the actor-sync credentials and every cache derived from the old database are
 * dropped, and the actor reopens as a brand-new local database. Local backups and device
 * preferences survive; the `before-sign-out` backup the account page takes first is a second copy.
 *
 * The server session ends INSIDE the reset, after the connection's final upload check and close:
 * a check that fails must still find the user signed in, able to retry. (Ending the session before
 * the reset also showed the signed-out auth screen early, which a slow reset then re-rendered under
 * a half-filled sign-in form — staging run 36079744481.)
 *
 * Keeping the prior device-account here would also let a later sign-in reuse the previous
 * account's live Turso connection instead of provisioning the newly-authenticated account.
 */
async function resetActorSyncForSignOut(
  queryClient: QueryClient,
  {
    discardUnsyncedStudy,
    imported = null,
  }: { discardUnsyncedStudy: boolean; imported?: ActorReplicaRebuildPrepared | null },
): Promise<void> {
  // Let an in-flight provisioning finish first (`signOutAndResetDevice` already waited once; this
  // keeps the reset safe on its own). On native it includes the sign-up guest handoff:
  // the moved-aside study is replayed into the new account and pushed only when the sync-mode
  // open completes, and until then it exists nowhere else. The purge below deletes that handoff,
  // so a sign-out tapped during the ~20s `database.actor.ensure` of a sign-up destroyed the guest
  // study for good (staging run 36061536484, guest-study-survives-sign-up: `actor database
  // CREATED (sign-up)` followed in the same second by `actor-db:sign-out-reset`). The wait is
  // bounded: a failure changes nothing here (the account is being discarded either way), and a
  // wedged provisioning must never keep the user signed in on a spinner.
  await waitForInFlightActorSyncProvisioning("sign-out reset");
  await queryClient.cancelQueries();
  // Bounded at the awaiter: the database lifecycle owns its load gate and keeps it until the
  // native close/purge/reopen really ends; this only guarantees the sign-out settles.
  await withDeadline(
    { deadline: SIGN_OUT_RESET_DEADLINE_MS, operation: "reset the actor database for sign-out" },
    () =>
      runWithActorDatabaseLoadsPaused(async () => {
        if (!discardUnsyncedStudy && holdsAccountStudyLocally()) {
          if (imported === null) {
            // The last word before the connection closes: anything written since the caller's
            // upload goes up too (usually nothing, so this is one empty push), or nothing is erased.
            await assertEveryLocalChangeUploaded(async () => getActiveActorTursoSync(), {
              deadlineMs: SIGN_OUT_FINAL_UPLOAD_DEADLINE_MS,
            });
          } else {
            // The import carried the study; this closes the connection and sends what was written
            // since. The purge below removes the retired generation with everything else.
            try {
              await retireActorReplicaForRebuild("sign-out", imported, {
                close: closeCachedActorDatabaseConnection,
                connection: getCachedActorDatabase()?.connection ?? null,
              });
            } catch (error) {
              throw new UnsyncedStudyDataError(
                `the last changes could not be imported: ${ensureError(error).message}`,
                { cause: error },
              );
            }
          }
        }
        // Close first so no handle holds the files, then purge them (web keeps its guest/actor split
        // and wipes at the next sign-in instead). Only then drop the account, so a concurrent open
        // cannot slip in between and reopen the old data locally.
        await closeCachedActorDatabaseConnection();
        // The sign-out is committed (the study is safe or explicitly discarded). Captured while
        // the session — and so the analytics identity — still belongs to the leaving account.
        captureAnalyticsEvent("account_signed_out", {
          discard_unsynced_study: discardUnsyncedStudy,
        });
        await withDeadline(
          { deadline: SIGN_OUT_REQUEST_DEADLINE_MS, operation: "end the server session" },
          () => getAuthClient().signOut(),
        );
        // Otherwise the signed-out user's support thread (and the email Crisp shows the operator)
        // stays attached to this device, and the next person to open Support reads their history.
        resetCrispChatSession();
        await resetActorDatabaseForSignOut();
        // Before the account is dropped, so no render sees "signed in without an account".
        clearAuthChangeSignal();
        setDeviceAccount(null);
        // The synchronous MMKV mirror of the old `user` row would otherwise paint the previous
        // account's settings until the new database's first query lands.
        clearActorUserCache();
        clearSyncFailure();
        pendingSignInActorReset = false;
        pendingGuestReplay = false;
        await reopenActorDatabaseConnectionInSyncMode();
      }),
  );
  // Inactive entries would otherwise keep the previous account's rows until GC; a clean install
  // has none. Active ones are reset (re-fetched) against the new, empty database — in the
  // background: sign-out is complete once the cache is cleared.
  queryClient.removeQueries({ type: "inactive" });
  startQueryReset(queryClient, "sign-out");
  publishAccountChange();
  // Last: a device without an account email is sent to the welcome gate (the native `(app)` layout
  // requires one), so it goes only once the device really is a clean install.
  setDeviceSetting("accountEmail", null);
}

/**
 * The actor DB connection is opened once at app launch. When the user signs in
 * for the first time, that connection was opened in local-only mode (no
 * device-account existed yet) — its writes can't sync. Reopen it IN PLACE in Turso
 * sync mode against the freshly-provisioned per-user database. No-op if the
 * connection is already synced (`getActiveActorTursoSync()` non-null).
 *
 * We swap in-place (close → clear the connection caches → reset queries) rather than
 * reloading the JS: `Updates.reloadAsync()` is broken on local staging builds
 * (expo-updates is misconfigured), which left the actor DB stuck in local-only mode
 * with an empty deck. The in-place swap removes that reload dependency everywhere.
 *
 * On a SIGN-IN (server is the source of truth), the local actor.db is wiped first
 * so the manual Load from Cloud action starts from a clean local actor DB instead
 * of the pre-migrated local file.
 */
async function reloadForActorSyncIfNeeded(
  queryClient: QueryClient,
  refreshCredentials = false,
): Promise<void> {
  // A sign-out/sign-in account switch can still have the prior account's native sync handle alive
  // when the new sign-in completes. The pending reset must win over that stale-handle fast path.
  if (!refreshCredentials && !pendingSignInActorReset && getActiveActorTursoSync() != null) {
    return;
  }
  // Quiesce reads and close the local-only handle before a sign-in reset deletes its file.
  // Deleting actor.db while active queries still hold the old handle leaves those queries reading
  // an empty replacement file, which surfaced as transient `no such table` failures after sign-in.
  await queryClient.cancelQueries();
  let reopenedDuringSignInReset = false;
  if (pendingSignInActorReset) {
    pendingSignInActorReset = false;
    try {
      // Native backs up the database file even when no connection is open in this session.
      const currentDatabase = getCachedActorDatabase()?.rawDatabase ?? null;
      // The safety backup is saved AFTER the old handle closes and before its files are reset:
      // with no connection left, nothing can be written that the backup would miss (native). Web
      // saves it before the swap, inside the same cross-tab backup job (`checkpoint` is a no-op).
      await withActorBackupCheckpoint(currentDatabase, "before-sign-in", async (checkpoint) => {
        await withDeadline(
          {
            deadline: DATABASE_SWAP_DEADLINE_MS,
            operation: "swap the actor database for sign-in",
          },
          () =>
            runWithActorDatabaseLoadsPaused(async () => {
              holdActorReadsForSignInBootstrap();
              await closeCachedActorDatabaseConnection();
              await checkpoint();
              await resetActorDatabaseForSignIn();
              await reopenActorDatabaseConnectionInSyncMode();
              reopenedDuringSignInReset = true;
            }),
        );
      });
      // NOTE: we deliberately do NOT drop the actor migration fingerprint here.
      // After the reload, the actor opens in sync mode and `connect()` BOOTSTRAPS
      // the account's cloud data — including a populated `__drizzle_migrations`
      // journal (synced from the device that created the account). The migration
      // mutation's verify-cache path already probes that in-DB journal before
      // trusting the fingerprint, so a surviving fingerprint is safe: journal
      // present + up to date → "verify-cache:hit" → migrate() is skipped and the
      // bootstrapped rows are preserved. If the wipe left a genuinely empty DB
      // (no bootstrap), the journal is absent → the verify path falls through to
      // migrate() and re-creates the schema. Dropping the fingerprint instead made
      // the dev "edited-migration" drop path fire (priorFingerprint=undefined +
      // a fully-applied bootstrapped journal), which WIPED the freshly-pulled
      // cloud deck — the empty-deck-after-web-sign-in bug.
    } catch (error) {
      // Native: the user keeps studying on the un-wiped local database from here, so a retry
      // carries it into the account (the sync-mode open replays a local file) instead of wiping
      // it. Web studies signed-out in a separate `guest.db`, so its retry still resets `actor.db`.
      pendingSignInActorReset = !guestReplayRunsDuringOpen;
      getSyncLog().error("failed to reset actor database for sign-in", error);
      throw error;
    }
  }
  // Confirm the session cookie is durably persisted BEFORE reopening — otherwise it can
  // race the async cookie write and the app comes back signed-out. (Provisioning above
  // already waited on it, but reset-app flows can re-enter here.)
  await waitForSessionCookie();
  // Swap the actor connection to Turso sync mode IN PLACE (no JS reload): close the
  // local-only handle + drop the connection caches, then reset the React Query cache so
  // the actor-data suspense query re-runs and re-opens the connection — now in sync mode
  // (the device-account is stored). A broad `resetQueries()` re-runs every active query;
  // sign-in is a natural full-refresh point, so this is acceptable, and it re-suspends the
  // AppLayout boundary briefly (the same UX the old reload produced, minus the restart).
  if (!reopenedDuringSignInReset) {
    // The native opener owns guest preservation, including restarts with no auth
    // flags. Close the old handle and invalidate caches under the same load gate.
    await withDeadline(
      { deadline: DATABASE_SWAP_DEADLINE_MS, operation: "reopen the actor database in sync mode" },
      () => runWithActorDatabaseLoadsPaused(reopenActorDatabaseConnectionInSyncMode),
    );
  }
  await resetQueriesDuringActorBootstrap(queryClient);

  // `resetQueries()` only schedules the suspense reads that lazily open the replacement native
  // connection. Keep the provisioning single-flight alive until that open publishes its sync
  // handle. Without this wait, a stale React effect can enter provisioning again in the gap,
  // clear the connection memo a second time, and null the first handle while its initial pull is
  // running (`Cannot read property 'applyChanges' of null`).
  // Join the complete open, including native guest replay and migrations. A
  // published SDK handle alone does not mean the provider cache is ready.
  await loadActorDatabaseBounded();
  if (getActiveActorTursoSync() == null) {
    throw new Error("Actor Turso sync handle did not open after the connection swap.");
  }

  // SIGN-UP only: carry the signed-out guest study into the account now that the
  // synced connection is live. Runs AFTER the sync handle is up because the rows
  // must be written THROUGH that connection to be CDC-captured (and therefore
  // pushable) — writing them anywhere else strands them locally forever.
  if (pendingGuestReplay) {
    pendingGuestReplay = false;
    if (!guestReplayRunsDuringOpen) {
      await replayGuestDataIntoAccount(queryClient);
    }
  }
}

/**
 * Carry `guest.db`'s rows into the freshly provisioned account through the import endpoint, then
 * pull them down (see `database.md`, "Accounts and guests"; native does the same inside its open,
 * `openWithGuestHandoff`). The import is named by the guest's contents, so a retry after a
 * failure changes nothing.
 *
 * Deliberately non-fatal: the account is already usable at this point, and `guest.db` is left
 * intact on any failure, so a retry (or a manual export) remains possible. Failing the whole
 * sign-up because the carry failed would be a worse outcome than a signed-in user whose guest
 * deck didn't come across — which we surface loudly in the log and via the sync-failure banner.
 */
async function replayGuestDataIntoAccount(queryClient: QueryClient): Promise<void> {
  try {
    const actorDatabaseModule = await import("@-/dev.improve.haniverse.database.actor");
    const guest = await withDeadline(
      { deadline: GUEST_REPLAY_DEADLINE_MS, operation: "open the guest database for the carry" },
      () => actorDatabaseModule.openGuestHandoffForExport(),
    );
    if (guest === null) {
      getSyncLog().info("no guest data to carry into the new account");
      await discardGuestHandoffDatabase();
      return;
    }
    let carried: GuestHandoffCarried;
    try {
      carried = await withDeadline(
        { deadline: GUEST_REPLAY_DEADLINE_MS, operation: "carry guest data into the account" },
        () => actorDatabaseModule.carryGuestStudyThroughImport(guest.all),
      );
    } finally {
      await guest.close().catch(() => {});
    }

    const sync = getActiveActorTursoSync();
    const cached = getCachedActorDatabase();
    if (sync == null || cached == null) {
      throw new Error("actor connection unavailable for the guest carry");
    }
    await withDeadline(
      { deadline: GUEST_REPLAY_PUSH_DEADLINE_MS, operation: "pull the carried guest data" },
      () => sync.pull(),
    );
    try {
      await withDeadline(
        { deadline: GUEST_REPLAY_DEADLINE_MS, operation: "retain the guest's level packs" },
        () =>
          runOwnedSqliteTransaction(cached.connection, (transaction) =>
            actorDatabaseModule.retainGuestCustomLevelPacks(
              transaction,
              carried.guestCustomLevelPacks,
            ),
          ),
      );
    } catch (error) {
      // The study rows are in; the packs are the one thing an insert cannot merge.
      getSyncLog().warn("the guest's level packs were not retained", error);
    }
    getSyncLog().info("carried the guest study into the new account");
    // Only now is the handoff copy redundant: the rows are in the account's cloud copy and in
    // this replica. Native deletes the moved-aside file; web keeps `guest.db` (no-op).
    await discardGuestHandoffDatabase();
    // The pulled rows were written outside React Query's knowledge, so refresh.
    await resetQueriesDuringActorBootstrap(queryClient);
  } catch (error) {
    getSyncLog().error("failed to carry guest data into the new account", error);
    setSyncFailure({
      at: Date.now(),
      kind: "push",
      message: `Your local study data could not be moved into your new account: ${ensureError(error).message}`,
    });
  }
}

const getSyncLog = onetime(() =>
  createPlainConsola({
    defaults: { tag: "sync" },
  }),
);

/** Join (or start) the actor database open, bounded. */
function loadActorDatabaseBounded(): Promise<unknown> {
  return withDeadline(
    { deadline: ACTOR_DATABASE_OPEN_DEADLINE_MS, operation: "open the actor database" },
    () => loadActorDatabase(),
  );
}

/** Read the stored session back from the auth server, bounded. */
function getStoredSession() {
  return withDeadline({ deadline: SESSION_CHECK_DEADLINE_MS, operation: "read the session" }, () =>
    getAuthClient().getSession({ query: { disableCookieCache: true } }),
  );
}

/**
 * Block until the better-auth/expo session cookie is durably persisted (in the SecureStore
 * jar), so the next authenticated request actually carries it. Right after sign-in the cookie
 * write is async; without this wait, an authenticated call (or a reopen) can race it and read
 * as signed-out. `getSession({ disableCookieCache: true })` reads the STORED cookie; a non-null
 * user means it's persisted. Bounded (each request and the whole wait); returns whether a session
 * was observed.
 */
async function waitForSessionCookie(): Promise<boolean> {
  try {
    await withDeadline(
      { deadline: SESSION_COOKIE_WAIT_DEADLINE_MS, operation: "wait for the session cookie" },
      (signal) =>
        pRetry(
          async () => {
            const { data } = await getStoredSession();
            if (data?.user == null) {
              throw new Error("session cookie not persisted yet");
            }
          },
          {
            factor: 1,
            maxTimeout: 200,
            minTimeout: 200,
            retries: 9,
            signal,
          },
        ),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Call `database.actor.ensure` with retry + backoff. The effect that drives provisioning does
 * NOT re-run on its own, so a single transient failure (the post-sign-in cookie race →
 * UNAUTHORIZED, or a network blip) would otherwise leave the device permanently un-provisioned
 * for this launch (no device-account → actor never enters sync mode → empty deck). We wait for
 * the session cookie first, then retry the call a few times. Throws the last error if every
 * attempt fails (the caller records a sync failure).
 *
 * NOTE: this deliberately does NOT abort on the effect cleaning up. Provisioning writes to a
 * module-level store and is guarded by a module-level in-flight flag, so it must run to completion
 * regardless of the driving component re-rendering/unmounting. An earlier `isDisposed` abort tied
 * to a per-effect flag killed provisioning the instant the post-sign-in re-render re-ran the effect
 * ("provisioning aborted: component unmounted") → empty deck.
 */
async function provisionSyncDbWithRetry(): Promise<{
  created?: boolean;
  ownerId: string;
  token: string;
  url: string;
}> {
  // The whole phase is bounded (`PROVISIONING_REQUEST_DEADLINE_MS`), and its signal stops any
  // further attempt once it expires, so a slow backend yields one visible, retryable failure
  // instead of an attempt loop that outlives everyone waiting on it.
  return withDeadline(
    { deadline: PROVISIONING_REQUEST_DEADLINE_MS, operation: "provision the actor sync database" },
    async (signal) => {
      // Ensure the session cookie is persisted before the FIRST attempt (the most common failure).
      await waitForSessionCookie();
      return pRetry(
        () =>
          withDeadline(
            { deadline: ENSURE_ATTEMPT_DEADLINE_MS, operation: "database.actor.ensure" },
            () => getOrpcClient.database.actor.ensure({ protocol: ACTOR_SYNC_PROTOCOL }),
          ),
        {
          factor: 1,
          maxTimeout: 0,
          minTimeout: 0,
          onFailedAttempt: async ({ attemptNumber, error, retriesLeft }) => {
            getSyncLog().warn(
              `database.actor.ensure attempt ${attemptNumber}/${ENSURE_MAX_ATTEMPTS} failed`,
              ensureError(error).message,
            );
            if (retriesLeft > 0) {
              // Re-confirm the cookie (an UNAUTHORIZED almost always means it wasn't ready yet),
              // then back off before the next attempt.
              await waitForSessionCookie();
              await delay(500 * attemptNumber, { signal });
            }
          },
          retries: ENSURE_MAX_ATTEMPTS - 1,
          signal,
        },
      );
    },
  );
}

/**
 * Provision the per-user Turso DB and reopen the actor connection in sync mode. Shared
 * by the reactive `useActorSync` effect (fire-and-forget) and the awaitable
 * `bootstrapActorSyncForSignIn` below (auth mutations start it after credentials succeed).
 *
 * The memoized wrapper below makes concurrent calls await one shared operation. Resolves once
 * the device-account is stored and the sync-mode connection has opened. Does not wait for the
 * initial pull — that is `bootstrapActorSyncForSignIn`'s job.
 */
async function runActorSyncProvisioningUncached(queryClient: QueryClient): Promise<void> {
  // Load the previous local generation before provisioning can change its account.
  // This also protects data when sign-in is the first screen opened after launch.
  if (pendingSignInActorReset && getDeviceSetting("isLocalBackupEnabled")) {
    await loadActorDatabaseBounded();
  }
  const previousAccount = getDeviceAccount();
  // The replica on this device belongs to ONE account (`ownerId`), and its credential does not
  // depend on a session. So whenever a session is established, compare the two:
  //   - the same account keeps the replica, and with it whatever it has not uploaded yet;
  //   - another account must never be served it: the replica is replaced.
  // An unreadable session fails the provisioning here, before anything is erased or reused.
  let ownerChanged = false;
  if (previousAccount?.ownerId !== undefined) {
    const { data } = await getStoredSession();
    const userId = data?.user?.id;
    if (userId === undefined) {
      throw new Error("The session could not be read after signing in. Try again.");
    }
    if (userId === previousAccount.ownerId) {
      if (pendingSignInActorReset) {
        getSyncLog().info("signed in again as this replica's account; keeping the replica");
        pendingSignInActorReset = false;
        releaseActorReadsForSignInBootstrap("same-owner");
      }
    } else {
      getSyncLog().warn("another account signed in; replacing the previous account's replica");
      ownerChanged = true;
      pendingSignInActorReset = true;
      pendingGuestReplay = false;
      // What the previous account has not uploaded goes up with ITS credential first. Best-effort:
      // the safety backup taken before the swap is the fallback until the import endpoint exists.
      const previousSync = getActiveActorTursoSync();
      if (previousSync !== null) {
        await withDeadline(
          {
            deadline: SIGN_OUT_FINAL_UPLOAD_DEADLINE_MS,
            operation: "upload the previous account's study",
          },
          () => previousSync.push(),
        ).catch((error: unknown) => {
          getSyncLog().warn("the previous account's unsent study could not be uploaded", error);
        });
      }
    }
  }
  if (!ownerChanged && isActorSyncProvisioned(previousAccount)) {
    // Already provisioned this session; just make sure the connection is sync-mode.
    await reloadForActorSyncIfNeeded(queryClient);
    return;
  }
  try {
    getSyncLog().info("provisioning per-user Turso sync database");
    const { created, ownerId, token, url } = await provisionSyncDbWithRetry();
    // Decide BEFORE the reopen: `reloadForActorSyncIfNeeded` consumes the flag.
    // Local study is carried into the account only when it belongs to nobody yet.
    const replayGuestData = previousAccount === null && isSignUpProvisioning(created);
    pendingGuestReplay = replayGuestData;
    getSyncLog().info(
      `actor database ${created === true ? "CREATED (sign-up)" : created === false ? "existed (sign-in)" : "create-state unknown (local dev)"}` +
        ` → ${replayGuestData ? "replaying guest data" : "no guest replay"}`,
    );
    if (previousAccount !== null || getDeviceAccount() == null) {
      // A LOCAL/loopback dev URL is deliberately NOT persisted (it's an ephemeral dev-stack port that
      // dies with the stack) — the account stores only the token and the open path re-derives the URL
      // from the live-stack env. A cloud (prod/staging) URL IS persisted. See `resolveActorSyncUrl`.
      setDeviceAccount({
        // Nothing of another account's record carries over.
        ...(ownerChanged ? null : previousAccount),
        ...buildProvisionedDeviceAccount({ ownerId, token, url }),
      });
    }
    getSyncLog().info("per-user Turso sync database provisioned");
    clearSyncFailure();
    // Reopen the actor connection in Turso sync mode now that the account exists (it
    // opened local-only at launch). Swapped in place (no JS reload). This FORCES sync
    // mode whenever signed in (see `reopenActorDatabaseConnectionInSyncMode`), so
    // the deck bootstraps regardless of the setting.
    await reloadForActorSyncIfNeeded(queryClient, true);
  } catch (error) {
    getSyncLog().error("failed to provision per-user Turso sync database", error);
    setSyncFailure({ at: Date.now(), kind: "push", message: ensureError(error).message });
    if (guestReplayRunsDuringOpen) {
      // The user keeps studying on the local database from here, so it holds the account's study
      // now: a retry must carry it into the account, never wipe it as the pre-sign-in guest copy.
      pendingSignInActorReset = false;
    }
    throw error;
  }
}

// Provisioning is global: it writes the module device-account store and swaps the one actor
// connection. Immediately after sign-in, both the auth form's awaited bootstrap and useActorSync's
// background effect can enter here. They MUST await the same operation. The former boolean guard
// made the second caller return immediately; when the auth form was second, it tried to prepare and
// navigate to Learn while the first caller was still closing the local SQLite handle, so navigation
// failed with DatabaseClosingError and left the signed-in user stranded on the auth form.
// `cache: false` retains only in-flight de-duplication: later calls still re-check the current account
// and connection state instead of pinning a completed provisioning result forever.
/** Every provisioning run still in flight, for `resetActorSyncForSignOut` to wait on. */
const inFlightActorSyncProvisioning = new Set<Promise<void>>();

function runTrackedActorSyncProvisioning(queryClient: QueryClient): Promise<void> {
  // Bounded as a whole so the single-flight entry (and this tracking) is released even if a step
  // bound fails: later callers then start a fresh run instead of joining one that never ends.
  const provisioning = withDeadline(
    { deadline: ACTOR_SYNC_PROVISIONING_DEADLINE_MS, operation: "actor sync provisioning" },
    () => runActorSyncProvisioningUncached(queryClient),
  ).catch((error: unknown) => {
    // Failures inside record themselves; the backstop's expiry must too, or the "Syncing data
    // from cloud" placeholder would outlive it with nothing to show.
    if (isDeadlineExceededError(error)) {
      setSyncFailure({ at: Date.now(), kind: "push", message: error.message });
    }
    throw error;
  });
  inFlightActorSyncProvisioning.add(provisioning);
  void provisioning
    .finally(() => inFlightActorSyncProvisioning.delete(provisioning))
    .catch(() => {
      // The caller owns the rejection; this chain only untracks it.
    });
  return provisioning;
}

const runActorSyncProvisioning = pMemoize(runTrackedActorSyncProvisioning, {
  cache: false,
  cacheKey: () => "actorSyncProvisioning",
});

/**
 * Awaitable sign-in bootstrap: provision the per-user Turso DB, reopen in sync mode, and
 * resolve once the account's deck has finished its initial remote pull. The auth form fires
 * this DETACHED when a session is established and navigates immediately — nothing user-facing
 * awaits it. Reads are held instead by the actor read gate `markSignInForActorReset`
 * installed, so screens needing the synced deck sit in their normal loading state until the
 * pull lands. Subsequent signed-in launches keep the replicated connection open
 * and automatically send local changes and receive cloud updates.
 *
 * Bounded internally (`waitForInitialPullCompletion` has a timeout) so a wedged sync settles
 * rather than pending forever. Safe to call more than once — provisioning is single-flight.
 */
async function bootstrapActorSyncForSignInUncached(queryClient: QueryClient): Promise<void> {
  const completionsBefore = getInitialPullCompletionCount();
  try {
    // Bounded as a whole so the single-flight below is released (and the read gate with it)
    // even if a step bound fails; the steps inside are bounded individually.
    await withDeadline(
      { deadline: SIGN_IN_BOOTSTRAP_DEADLINE_MS, operation: "sign-in actor bootstrap" },
      async () => {
        await runActorSyncProvisioning(queryClient);
        // The reopen re-runs the actor-data query, which reopens in sync mode; the sync effect
        // then fires the initial pull and bumps the completion counter when it drains. Wait for
        // that edge (or a pull failure / timeout) so the settle outcome is logged.
        const outcome = await waitForInitialPullCompletion(completionsBefore);
        getSyncLog().info("sign-in actor bootstrap settled", { outcome });
        releaseActorReadsForSignInBootstrap(outcome);
        publishAccountChange();
      },
    );
  } catch (error) {
    releaseActorReadsForSignInBootstrap("failed");
    throw error;
  }
}

// The route can unmount as soon as authentication succeeds. Its bootstrap and
// the root recovery effect must share completion, including gate release and
// the cross-tab publication, rather than only sharing database provisioning.
export const bootstrapActorSyncForSignIn = pMemoize(bootstrapActorSyncForSignInUncached, {
  cache: false,
  cacheKey: () => "actorSyncBootstrap",
});

/**
 * Retry a sign-in bootstrap that failed or timed out (its failure sits in sync-status). Clears the
 * recorded failure so the preparing state and the placeholder reflect the new attempt, then runs
 * the same single-flight bootstrap the auth forms start.
 */
export async function retryActorSyncBootstrap(queryClient: QueryClient): Promise<void> {
  clearSyncFailure();
  await bootstrapActorSyncForSignIn(queryClient);
}

/**
 * The sync gateway refused this device's credential (it was revoked, or the server's signing key
 * changed). Ask for a new one with the current session and reopen the SAME replica with it:
 * nothing local is erased, and unsent changes upload with the new credential.
 *
 * Resolves `false` when the device is no longer signed in (or is signed in as someone else): then
 * only signing in again helps, and the replica still waits untouched.
 */
async function refreshActorSyncCredentialUncached(queryClient: QueryClient): Promise<boolean> {
  const account = getDeviceAccount();
  if (account === null) {
    return false;
  }
  const { data } = await getStoredSession();
  const userId = data?.user?.id;
  if (userId === undefined || (account.ownerId !== undefined && account.ownerId !== userId)) {
    setSyncFailure({
      at: Date.now(),
      kind: "push",
      message: "Sign in again to keep syncing. Your study is saved on this device.",
    });
    return false;
  }
  getSyncLog().info("refreshing the actor sync credential");
  const { ownerId, token, url } = await provisionSyncDbWithRetry();
  setDeviceAccount({
    ...account,
    ...buildProvisionedDeviceAccount({ ownerId, token, url }),
  });
  // The engine holds the old credential: reopen the same replica with the new one.
  await reloadForActorSyncIfNeeded(queryClient, true);
  return true;
}

export const refreshActorSyncCredential = pMemoize(refreshActorSyncCredentialUncached, {
  cache: false,
  cacheKey: () => "actorSyncCredential",
});

/**
 * Replace the sync engine with a fresh one on the same replica (see `actor-sync-supervisor.ts`):
 * the response to an engine that keeps failing for no reason the gateway named.
 */
export async function reopenActorSyncConnection(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries();
  await withDeadline(
    { deadline: DATABASE_SWAP_DEADLINE_MS, operation: "reopen the actor database" },
    () => runWithActorDatabaseLoadsPaused(reopenActorDatabaseConnectionInSyncMode),
  );
  startQueryReset(queryClient, "sync engine reopen");
}

async function rebuildActorSyncReplicaUncached(
  queryClient: QueryClient,
  reason: string,
): Promise<void> {
  const log = getSyncLog();
  if (Date.now() - lastReplicaRebuildStartedAt < REPLICA_REBUILD_FLOOR_MS) {
    log.warn("a replica rebuild was asked for right after one; not rebuilding again", { reason });
    return;
  }
  lastReplicaRebuildStartedAt = Date.now();
  // The export and import run first, with queries alive; the swap itself cancels them.
  log.warn("rebuilding the actor replica", { reason });
  await queryClient.cancelQueries();
  const result = await rebuildActorDatabase(reason);
  log.info("actor replica rebuilt", result);
  startQueryReset(queryClient, "replica rebuild");
}

/**
 * Replace the replica with a fresh download of the cloud copy, after carrying its unsent changes
 * there through the import (see `actor-sync-supervisor.ts` and `database.md`, "Device: sync
 * supervisor and rebuild"): the response to an engine that keeps failing after a reopen, to a
 * replica the engine cannot use, and the way a rebuild cut short by a crash is finished. One at a
 * time: a second request while one runs joins it.
 */
export const rebuildActorSyncReplica = pMemoize(rebuildActorSyncReplicaUncached, {
  cache: false,
  cacheKey: () => "actorSyncReplicaRebuild",
});

/**
 * Ensure the actor DB is on a live Turso SYNC connection, reopening it in sync mode if it's
 * currently local-only (for example after provisioning failed). Resolves once `getActiveActorTursoSync()`
 * is non-null (a usable sync handle) or a bounded wait elapses.
 *
 * Backs explicit cloud actions and recovery retries as well as automatic sync:
 * "Save/Load from Cloud" buttons and sign-out's upload proof. All
 * need the connection swapped to sync mode BEFORE they push/pull; a plain local-only connection has
 * no remote handle. No-op (returns false) when signed out — there's no account to sync.
 *
 * A device that IS signed in but has no account database (its sign-in provisioning failed, and
 * nothing retries that in-session) is provisioned here, which throws if that fails again. Until
 * this, "Save to Cloud" — the sync-failure banner's own advice — could never recover it, and the
 * study the user kept doing stayed local-only (staging 2026-10-02).
 */
export async function ensureActorSyncConnected(queryClient: QueryClient): Promise<boolean> {
  if (!isActorSyncProvisioned(getDeviceAccount())) {
    const { data } = await getStoredSession();
    if (data?.user == null) {
      return false;
    }
    if (guestReplayRunsDuringOpen && inFlightActorSyncProvisioning.size === 0) {
      // This device has been studying on its local database since the sign-in, so that study is
      // the account's now: carry it in (the native sync-mode open replays a local file), never
      // wipe it as the pre-sign-in guest copy.
      pendingSignInActorReset = false;
    }
    await runActorSyncProvisioning(queryClient);
    return getActiveActorTursoSync() != null;
  }
  if (getActiveActorTursoSync() != null) {
    return true;
  }
  // Reopen in sync mode and wait for the swapped
  // connection's handle to come up. The reopen re-runs the actor-data query → reopens the
  // connection → the sync effect calls `getActiveActorTursoSync`.
  // Active queries still reference the current connection. Cancel them before the reopen closes
  // that handle; resetQueries below will restart the active reads against the replacement.
  await queryClient.cancelQueries();
  await withDeadline(
    { deadline: DATABASE_SWAP_DEADLINE_MS, operation: "reopen the actor database in sync mode" },
    () => reopenActorDatabaseConnectionInSyncMode(),
  );
  // Only the actor-data query's refetch matters (it reopens the connection); the handle poll below
  // governs completion, so do not wait for every active screen's refetch.
  startQueryReset(queryClient, "sync reopen");
  const POLL_INTERVAL_MS = 150;
  const MAX_WAIT_MS = 20_000;
  const start = Date.now();
  while (Date.now() - start < MAX_WAIT_MS) {
    if (getActiveActorTursoSync() != null) {
      return true;
    }
    await delay(POLL_INTERVAL_MS);
  }
  getSyncLog().warn("ensureActorSyncConnected: sync handle did not come up within budget");
  return false;
}

export function useActorSync(): ActorSyncState {
  useCrossTabAccountChanges();
  const [existingAccount] = useDeviceAccount();
  const hasAccount = isActorSyncProvisioned(existingAccount);
  const queryClient = useQueryClient();
  const { failure } = useSyncStatus();
  // Re-render (→ re-run the effect) whenever an auth-success path bumps the signal, so
  // provisioning fires even on builds where `useSession()` doesn't react to sign-in (Android
  // Release — see the `authChangeSignal` comment above).
  // `getAuthChangeSnapshot` is a pure module-level read (returns the `authChangeSignal` counter,
  // 0 at rest), so it is a valid `getServerSnapshot` too. Passing it as the third argument keeps web
  // server rendering (`EXPO_WEB_OUTPUT=server`) from erroring with "Missing getServerSnapshot, which
  // is required for server-rendered content" — which aborts SSR to a blank client-only render.
  const authChange = useSyncExternalStore(
    subscribeToAuthChange,
    getAuthChangeSnapshot,
    getAuthChangeSnapshot,
  );

  // Auth success paths signal explicitly, so do not subscribe to better-auth's session hook here.
  // This hook is mounted above the app route content; an eager session read makes signed-out
  // startup build/fetch `/api/auth/*` and run Sentinel before the first screen can settle.
  const signedIn = authChange > 0;

  useEffect(() => {
    if (hasAccount || signedIn || failure?.kind === "push") {
      return;
    }

    // This is only a recovery path for a persisted server session with no local device account.
    // Keep it well after first route paint so signed-out startup stays focused on local data.
    const timeoutId = setTimeout(() => {
      void (async () => {
        try {
          const { data } = await getStoredSession();
          if (data?.user != null && !isActorSyncProvisioned(getDeviceAccount())) {
            notifyAuthSessionEstablished();
          }
        } catch {
          // Signed-out startup should stay cheap and quiet; auth surfaces report real failures.
        }
      })();
    }, 10_000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [failure?.kind, hasAccount, signedIn]);

  useEffect(() => {
    if (!signedIn) {
      return;
    }
    if (hasAccount) {
      return;
    }
    // Fire-and-forget provisioning (the awaitable version is `bootstrapActorSyncForSignIn`, which
    // auth mutations also start after credentials succeed). `database.actor.ensure` is an
    // AUTHENTICATED oRPC call retried with backoff inside `runActorSyncProvisioning` (the
    // post-sign-in cookie write is async → the first attempt can 401). The shared helper carries
    // the module-level single-flight guard, so this and the auth-mutation bootstrap can safely race.
    // A rejection here is already recorded as a sync failure inside the helper; swallow it so the
    // unhandled rejection doesn't surface.
    void bootstrapActorSyncForSignIn(queryClient).catch(() => {});
  }, [signedIn, hasAccount, queryClient]);

  return { isPreparingActorSync: signedIn && !hasAccount && failure?.kind !== "push" };
}
